import React from 'react';
import {
  Box,
  Title,
  Paper,
  Grid,
  Text,
  Container,
  Group,
  Stack
} from '@mantine/core';

const SOATemplate = ({ data, resident }) => {
  if (!data) return null;

  // Helper to safely format numbers
  const formatMoney = (amount) => {
    if (!amount || amount === 0) return ''; 
    return Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Helper for Total
  const formatTotal = (amount) => {
      return Number(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fullName = resident 
    ? `${resident.firstName || ''} ${resident.lastName || ''}`.trim() || 'Name Not Available'
    : 'Name Not Available';

  const address = resident?.address 
    ? `Blk ${resident.address.block || ''} Lot ${resident.address.lot || ''} ${resident.address.street || ''}`.trim()
    : 'Address Not Available';

  return (
    // Wrapper for screen responsiveness
    <div style={{ width: '100%', overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
        <Container size="sm" style={{ padding: 0, minWidth: '600px', backgroundColor: 'white' }}>
        
        {/* Compact padding to ensure single page fit */}
        <Paper className="soa-container" p="lg" withBorder style={{ 
            backgroundColor: 'white', 
            fontFamily: 'Arial, sans-serif', 
            color: '#000', 
            minHeight: '800px', // Visual height on screen
            border: '1px solid #dee2e6' 
        }}>
            
            {/* --- HEADER SECTION (Compacted) --- */}
            <Stack gap={2} align="center" mb="md">
                <Title order={4} style={{ textTransform: 'uppercase', textAlign: 'center', fontSize: '16px', lineHeight: 1.2 }}>
                    BANCOM LIFE HOMEOWNER'S ASSOCIATION, INC.
                </Title>
                <Text size="xs">(Non Stock Profit Organization)</Text>
                <Text size="xs">Bancom Plaza, Gulod Malaya 1850, San Mateo, Rizal, Philippines</Text>
                <Text size="xs">Tel. No. 8706-5135 * Non-VAT Reg. TIN: 237-177-709-0000</Text>
            </Stack>

            {/* DOCUMENT TITLE */}
            <Group justify="center" mb="md">
                <Title order={3} style={{ textTransform: 'uppercase', letterSpacing: '1px', textDecoration: 'underline', fontSize: '20px' }}>STATEMENT OF ACCOUNT</Title>
            </Group>

            {/* INFO FIELDS (Reduced Spacing) */}
            <Stack gap="xs" mb="lg">
                <Group gap="xs">
                    <Text fw={700} w={80} size="sm">NAME:</Text>
                    <Box style={{ borderBottom: '1px solid black', flex: 1, paddingLeft: 5, fontWeight: 600, fontSize: '14px' }}>{fullName}</Box>
                </Group>
                <Group gap="xs">
                    <Text fw={700} w={80} size="sm">ADDRESS:</Text>
                    <Box style={{ borderBottom: '1px solid black', flex: 1, paddingLeft: 5, fontSize: '14px' }}>{address}</Box>
                </Group>
                
                <Grid gutter="md">
                    <Grid.Col span={6}>
                        <Group gap="xs">
                            <Text fw={700} w={80} size="sm">TIN:</Text>
                            <Box style={{ borderBottom: '1px solid black', flex: 1, marginTop: 5 }}></Box>
                        </Group>
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Group gap="xs">
                            <Text fw={700} size="sm">BUSINESS STYLE:</Text>
                            <Box style={{ borderBottom: '1px solid black', flex: 1, marginTop: 5 }}></Box>
                        </Group>
                    </Grid.Col>
                </Grid>
            </Stack>

            {/* BILLING PERIOD & DUE DATE */}
            <Grid mb="lg">
                <Grid.Col span={6}></Grid.Col>
                <Grid.Col span={6}>
                    <Group justify="flex-end" mb={5} gap="xs">
                        <Text fw={700} size="sm">BILLING PERIOD:</Text>
                        <Box style={{ borderBottom: '1px solid black', width: '140px', textAlign: 'center', fontSize: '14px' }}>
                            {data.billingPeriod}
                        </Box>
                    </Group>
                    <Group justify="flex-end" gap="xs">
                        <Text size="sm">Due Date</Text>
                        <Box style={{ borderBottom: '1px solid black', width: '140px', textAlign: 'center', fontSize: '14px' }}>
                            {data.dueDate ? new Date(data.dueDate).toLocaleDateString() : 'N/A'}
                        </Box>
                    </Group>
                </Grid.Col>
            </Grid>

            {/* BODY - ASSOCIATION DUES */}
            <Box mb="lg">
                <Title order={5} align="center" mb="md" style={{ textTransform: 'uppercase' }}>ASSOCIATION DUES</Title>

                {/* Current Charges */}
                <Grid align="flex-end" mb="xs">
                    <Grid.Col span={7}>
                        <Text fw={600} size="sm">Current Charges</Text>
                    </Grid.Col>
                    <Grid.Col span={1}><Text size="sm">Php</Text></Grid.Col>
                    <Grid.Col span={4}>
                        <Box style={{ borderBottom: '1px solid black', textAlign: 'right', paddingRight: 5, minHeight: '18px', fontSize: '14px' }}>
                            {formatMoney(data.currentCharges)}
                        </Box>
                    </Grid.Col>
                </Grid>

                {/* Previous Unpaid Section */}
                <Box mb="xs">
                    <Text fw={600} size="sm">Previous Unpaid Amount:</Text>
                    <Text size="xs" mt={2}>Period covered:</Text>

                    <Grid align="flex-end">
                        <Grid.Col span={7}>
                            <Box style={{ borderBottom: '1px solid black', height: 18 }}></Box>
                        </Grid.Col>
                        <Grid.Col span={1}><Text size="sm">Php</Text></Grid.Col>
                        <Grid.Col span={4}>
                            <Box style={{ borderBottom: '1px solid black', textAlign: 'right', paddingRight: 5, minHeight: '18px', fontSize: '14px' }}>
                                {formatMoney(data.previousUnpaidAmount)}
                            </Box>
                        </Grid.Col>
                    </Grid>
                </Box>

                {/* Reduced Filler Lines to 2 to save space */}
                {[1, 2].map((i) => (
                    <Grid align="flex-end" mb="xs" key={i}>
                        <Grid.Col span={7}><Box style={{ borderBottom: '1px solid black', height: 18 }}></Box></Grid.Col>
                        <Grid.Col span={1}><Text size="sm">Php</Text></Grid.Col>
                        <Grid.Col span={4}><Box style={{ borderBottom: '1px solid black', height: 18 }}></Box></Grid.Col>
                    </Grid>
                ))}

                {/* OTHERS SECTION */}
                <Box style={{ marginTop: '10px' }}> 
                    <Text fw={600} size="sm">Others:</Text>
                    {/* Reduced to 2 lines */}
                    {[1, 2].map((i) => (
                        <Grid align="flex-end" mb="xs" key={i}>
                            <Grid.Col span={7}><Box style={{ borderBottom: '1px solid black', height: 18 }}></Box></Grid.Col>
                            <Grid.Col span={1}><Text size="sm">Php</Text></Grid.Col>
                            <Grid.Col span={4}><Box style={{ borderBottom: '1px solid black', textAlign: 'right', paddingRight: 5, minHeight: '18px' }}></Box></Grid.Col>
                        </Grid>
                    ))}
                </Box>
            </Box>

            {/* TOTAL */}
            <Grid mb="xl" align="flex-end">
                <Grid.Col span={6} offset={2}>
                    <Text fw={700} align="right" size="sm">TOTAL AMOUNT DUE</Text>
                </Grid.Col>
                <Grid.Col span={1}><Text fw={700} size="sm">Php</Text></Grid.Col>
                <Grid.Col span={3}>
                    <Box style={{ borderBottom: '3px double black', textAlign: 'right', fontWeight: 'bold', paddingRight: 5, fontSize: '16px' }}>
                        {formatTotal(data.totalAmountDue)}
                    </Box>
                </Grid.Col>
            </Grid>

            {/* FOOTER */}
            <Box mt={30}>
                <Text fw={700} size="sm" mb="sm">TO OUR VALUED HOMEOWNER:</Text>
                <Text size="sm" style={{ fontStyle: 'italic' }}>
                    Please disregard this notice if payment has been made.
                </Text>
                
                <Text size="xs" c="dimmed" align="center" mt={40}>
                    Note: Generated on {new Date().toLocaleString('en-PH')}. Please visit the office for further inquiries.
                </Text>
            </Box>

        </Paper>
        </Container>
    </div>
  );
};

export default SOATemplate;